import { Trophy, XCircle, History, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import CryptoIcon from '../CryptoIcon';
import { formatPair } from '../../utils/commodities';

const usd = (v) => `$${Math.abs(Number(v || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const price = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 })}`);

const Cell = ({ label, value, cls = 'text-white', testid }) => (
  <div className="p-3 rounded-xl bg-gray-900/60">
    <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">{label}</p>
    <p className={`font-semibold ${cls}`} data-testid={testid}>{value}</p>
  </div>
);

export const TradeReportDialog = ({ report, onClose, onViewHistory }) => {
  if (!report) return null;
  const isBinary = report.kind === 'binary';
  const won = isBinary ? report.status === 'won' : true;
  const profit = isBinary ? report.actual_profit : (report.actual_profit ?? report.expected_profit);
  const payout = isBinary ? (won ? report.amount + profit : 0) : report.amount + (profit || 0);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`!bg-[#0b1120] text-white sm:max-w-md border ${won ? 'border-emerald-500/40' : 'border-red-500/40'}`} data-testid="trade-report-dialog">
        <DialogHeader className="!bg-transparent text-center sm:text-center">
          <div className={`mx-auto mb-2 w-14 h-14 rounded-full flex items-center justify-center ${won ? 'bg-emerald-500/15' : 'bg-red-500/15'}`}>
            {won ? <Trophy className="h-7 w-7 text-emerald-400" /> : <XCircle className="h-7 w-7 text-red-400" />}
          </div>
          <DialogTitle className="text-center text-2xl" data-testid="trade-report-title">
            {isBinary ? (won ? 'Trade Won' : 'Trade Lost') : 'Arbitrage Completed'}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="text-center text-gray-400 flex items-center justify-center gap-2">
            {isBinary ? <CryptoIcon symbol={report.crypto_symbol} size={20} /> : <Sparkles className="h-4 w-4 text-teal-300" />}
            {isBinary ? `${formatPair(report.crypto_symbol)} · ${(report.prediction || '').toUpperCase()} · ${report.duration_seconds}s` : `AI Arbitrage · ${report.period_hours}h`}
            </div>
          </DialogDescription>
        </DialogHeader>
        <p className={`!bg-transparent text-center text-4xl font-bold ${profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="trade-report-profit">
          {profit >= 0 ? '+' : '-'}{usd(profit)}
        </p>
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
          <Cell label="Amount" value={usd(report.amount)} />
          <Cell label="Payout" value={usd(payout)} cls="text-amber-300" testid="trade-report-payout" />
          {isBinary && <Cell label="Entry price" value={price(report.entry_price)} />}
          {isBinary && <Cell label="Exit price" value={price(report.exit_price)} />}
        </div>
        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} data-testid="trade-report-close-btn" className="flex-1 py-3 rounded-xl bg-gray-800 hover:bg-gray-700 font-semibold transition-colors">Close</button>
          <button type="button" onClick={onViewHistory} data-testid="trade-report-view-history-btn" className="flex-1 py-3 rounded-xl bg-purple-600 hover:bg-purple-500 font-semibold flex items-center justify-center gap-2 transition-colors">
            <History className="h-4 w-4" /> View in History
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TradeReportDialog;
